import { DynamicOptions, SelectOptionBO } from '@/shared/components/AdvancedExpressionBuilder';

export const Operator = {
  // binary ones:
  /** The field must be equals to some other. */
  equals: 'equals',
  /** The field must not be equals to some other. */
  not_equals: 'not_equals',
  /** The field has the size of a given value. */
  has_size_equals_to: 'has_size_equals_to',
  /** The field is greater than a given value */
  greater_than: 'greater_than',
  /** The field is smaller than a given value. */
  less_than: 'less_than',
  /** The field contains a defined value. */
  includes: 'includes',
  /** The field does not contain a defined value */
  not_includes: 'not_includes',
  /** The field must be between two values (inclusive). */
  between: 'between',

  // unary ones:
  /** The field is fulfilled or not. */
  present: 'present',
  /** Same as 'not present'. */
  blank: 'blank'
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Operator = (typeof Operator)[keyof typeof Operator];

/** */
export type IExpression = {
  fieldIdentifier: string;
  type: string;
  operation: Operator;
  /** Is optional for unary operators. */
  value?: unknown;
};

export type IExpressionScenarioStructure = number[];
export type IConditionDefinition = {
  /** only the version, just ignore it */
  $version: any;
  /** A list of expressions that should be evaluated. */
  expressions: IExpression[];
  /**
   * Define how each expression in `expressions` array related with each other
   * in a logical sense.
   * Is a matrix on each row represents a logical AND and each column represents
   * a logical OR.
   * Each row in this matrix is called 'scenario'.
   */
  expressionsStructure: IExpressionScenarioStructure[];
};

export interface NotificationSetupTemplate {
  id: string;
  name: string;
  description?: string;
  source: string;
  isActive: boolean;
  conditions?: IConditionDefinition;
  mongoQuery?: Record<string, any>;
  recipientUserIds?: RecipientUserIds;
  notificationMessageTemplateId: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface RecipientUserIds {
  static?: string[];
  contextual?: string[];
}

// DTOs for Notification Message Templates
export interface NotificationMessageTemplate {
  id: string;
  name: string;
  description?: string;
  channel: string;
  ownerOrganizationId?: string;
  payloadDefinition: Record<string, any>;
}

// DTOs for Organization Notification Triggers
export interface OrganizationNotificationTrigger {
  id: string;
  ownerOrganizationId: string | null;
  createdByUserId: string;
  name: string;
  description?: string;
  source: string;
  conditions?: IConditionDefinition;
  mongoQuery?: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  notificationDispatchSetups?: NotificationDispatchSetup[];
}

// DTOs for Notification Dispatch Setup
export interface NotificationDispatchSetup {
  id: string;
  notificationTriggerId: string;
  notificationMessageTemplateId: string;
  createdByUserId: string;
  recipientUserIds: string[];
  additionalPayloadDefinitionConfig?: AdditionalPayloadDefinitionConfig;
  createdAt: string;
  updatedAt: string;
}

export interface AdditionalPayloadDefinitionConfig {
  sendingBehavior?: string;
}

export interface FieldComponents {
  nodes?: SelectOptionBO[];
  dynamicOptions?: DynamicOptions;
}

export enum NotificationEngineFieldTypes {
  address = 'address',
  text = 'text',
  boolean = 'boolean',
  currency = 'currency',
  multiselect = 'multiselect',
  select = 'select',
  textArea = 'textArea',
  date = 'date',
  numeric = 'numeric',
  file = 'file'
}

export interface NotificationEngineSourceDTO {
  fieldIdentifier: string;
  fieldType: NotificationEngineFieldTypes;
  description: string;
  components?: FieldComponents;
}

// event retry types

export enum EVENT_RETRY_STATUS {
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  DISMISSED = 'DISMISSED'
}

export interface EventFailureDTO {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  organizationId?: string;
  payload: any;
  errorDetailed: any;
  originalTraceId?: string;
  status: EVENT_RETRY_STATUS;
  attemptCount: number;
  lastAttemptAt?: Date | null;
  source?: string | null;
  detailType?: string | null;
  messageId?: string | null;
  requestedByUserId?: string | null;
}

export interface FetchEventPaginatedOptionsDTO {
  limit?: number;
  page?: number;
  sortAttributes?: string[];
  sortDirections?: string[];
  status?: EVENT_RETRY_STATUS;
}

// Pagination types
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  currentPage: number;
  itemsPerPage: number;
}

interface AlternativePaginatedResult<T> {
  results: T[];
  total: number;
  page_total: number;
}

export type PaginatedNotificationTriggerDTO = PaginatedResult<OrganizationNotificationTrigger>;
export type PaginatedNotificationSetupTemplateDTO = PaginatedResult<NotificationSetupTemplate>;
export type PaginatedEventFailureDTO = AlternativePaginatedResult<EventFailureDTO>;
